import { DebouncedInputField } from "@/components/DebouncedInputField";
import { CheckboxContainer } from "@/components/layout/CheckboxContainer";
import { CheckListContainer } from "@/components/layout/ChecklistContainer";
import { getTranslation } from "@/features/questionnaire/utils";
import styled from "@emotion/styled";
import { Checkbox, Hyperlink } from "@repo/skapa";
import { t } from "@repo/translation";
import { useSettings } from "@repo/ui";
import { type ChangeEvent, useState } from "react";

import { type QuestionsProps } from "../types";

const FreeTextContainer = styled.div`
  margin-top: 1rem;
`;

export const ChecklistQuestion = ({
  answer,
  onAnswerIdAnswered,
  question,
  onFreeTextAnswered,
}: QuestionsProps) => {
  const { language } = useSettings();
  const [extraInputValues, setExtraInputValues] = useState<
    Record<string, string>
  >(answer?.answerFreeText ? JSON.parse(answer.answerFreeText) : {});
  const selectedAnswerIds = answer?.answerIds || [];
  const handleOptionChange = (option: string, isChecked: boolean) => {
    const updatedSelectedIds = isChecked
      ? [...selectedAnswerIds, option]
      : selectedAnswerIds.filter((id: string) => id !== option);
    onAnswerIdAnswered?.(updatedSelectedIds);
  };

  const handleExtraInputChange = (answerId: string, value: string) => {
    const updatedValues = {
      ...extraInputValues,
      [answerId]: value,
    };
    setExtraInputValues(updatedValues);
    onFreeTextAnswered?.(JSON.stringify(updatedValues));
  };

  if (!question?.answers) {
    return null;
  }

  return (
    <CheckListContainer>
      {question.answers.map((option, index) => (
        <CheckboxContainer key={option.answer_id_key ?? `index-${index}`}>
          <Checkbox
            subtle
            id={option.answer_id_key}
            checked={selectedAnswerIds.includes(option.answer_id_key)}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleOptionChange(option.answer_id_key, e.target.checked)
            }
            value={option.answer_id_key}
            label={
              <>
                {getTranslation(
                  option.answer_translation ?? [],
                  language,
                  option.answer_name
                )}{" "}
                {option.answer_mediaSource ? (
                  <Hyperlink
                    url={option.answer_mediaSource}
                    newWindow
                    text={t("label.form_link")}
                  />
                ) : null}
              </>
            }
          />
          {selectedAnswerIds.includes(option.answer_id_key) &&
            option.answer_extra_inputs && (
              <FreeTextContainer>
                {option.answer_extra_inputs.map((extraInput) => (
                  <DebouncedInputField
                    key={`${option.answer_id_key}-${extraInput.answer_extra_inputs_id_key}`}
                    label={getTranslation(
                      extraInput.answer_extra_inputs_translation ?? [],
                      language,
                      extraInput.answer_extra_input_name || ""
                    )}
                    value={
                      extraInputValues[
                        `${option.answer_id_key}-${extraInput.answer_extra_inputs_id_key}`
                      ] || ""
                    }
                    onChange={(value) =>
                      handleExtraInputChange(
                        `${option.answer_id_key}-${extraInput.answer_extra_inputs_id_key}`,
                        value
                      )
                    }
                  />
                ))}
              </FreeTextContainer>
            )}
        </CheckboxContainer>
      ))}
    </CheckListContainer>
  );
};
